import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '../lib/queryClient';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Columns3,
  KanbanSquare,
  MessageSquareText,
  Settings,
  PanelLeftClose,
  PanelRightClose,
  Radar,
  FolderKanban,
  ListChecks,
  Search,
  X,
  CalendarDays,
  ArrowLeft,
  CheckCircle2,
  Circle,
  AlertTriangle,
  AlertCircle,
  Loader2,
  Hammer,
  Share2,
  BarChart3,
} from 'lucide-react';
import { TaskingBotDashboard, TaskingBotSettingsModal } from '../components/TaskingBotDashboard';
import { ShareDialog } from '../components/ShareDialog';
import { TaskFlow } from '../components/TaskingBoard/TaskFlow';
import { TaskSchedule } from '../components/TaskingBoard/TaskSchedule';
import { TaskingBotAgentsPanel } from '../src/components/TaskingBotAgentsPanel';
import { TaskflowView } from '../components/TaskflowView';
import { TaskingBotAvatarPortal } from '../components/TaskingBotAvatarPortal';
import { TaskingBotChat, type MainPanelTab } from '../components/TaskingBotChat';
import {
  TaskingBotChat as TaskingBotFocusedChat,
  type FocusTaskContext,
  type TaskHeaderInfo,
} from '../src/components/TaskingBotChat';
import {
  TaskingBotTasksStatus,
  type OpenInChatTask,
} from '../components/TaskingBotTasksStatus';
import { TaskPlex } from '../components/TaskPlex';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '../components/Resizable';
import { TaskingBotSidebar } from '../components/TaskingBotSidebar';
import { TaskingBotProjectDialog } from '../components/TaskingBotProjectDialog';
import { TaskDetailModal } from '../components/TaskDetailModal';
import { ChatAttachmentsLibrary } from '../components/ChatAttachmentsLibrary';
import { SessionNavToolbar } from '../components/SessionNavToolbar';
import { useIsMobile } from '../helpers/useIsMobile';
import { type TaskingBotProject } from '../helpers/useTaskingBotProjects';
import { useTaskingBotTasks, type TaskingBotTask } from '../helpers/useTaskingBotTasks';
import superjson from 'superjson';
import styles from './taskingbot.module.css';
import { useTranslation } from '../helpers/useLocalization';
import { useAuth } from '../helpers/useAuth';
import { OnboardingTutorial, useShowSectionOnboarding } from '../components/OnboardingTutorial';
// DesktopViewerToggle is triggered from chat input bar via custom event

function getTaskStatusIcon(status: string) {
  switch (status) {
    case 'completed': return <CheckCircle2 size={16} style={{ color: 'var(--success, #22c55e)' }} />;
    case 'running': case 'in_progress': return <Loader2 size={16} className="animate-spin" style={{ color: 'var(--info, #3b82f6)' }} />;
    case 'failed': return <AlertCircle size={16} style={{ color: 'var(--error, #ef4444)' }} />;
    case 'waiting_for_input': return <AlertTriangle size={16} style={{ color: 'var(--warning, #f59e0b)' }} />;
    default: return <Circle size={16} style={{ color: 'var(--foreground)', opacity: 0.4 }} />;
  }
}

type TaskingBotView = 'workspace' | 'board' | 'tasksuite' | 'roadmap' | 'agents';
type MobilePanel = 'tasks' | 'chat' | 'assets';

export default function TaskingBotPage() {
  const isMobile = useIsMobile();
  const { authState } = useAuth();
  const currentUserId = authState.type === 'authenticated' ? authState.user?.id : null;
  const showTutorial = useShowSectionOnboarding('taskingbot', currentUserId);
  const [tutorialDismissed, setTutorialDismissed] = useState(false);
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { taskId: routeTaskId } = useParams<{ taskId?: string }>();
  const [showSettings, setShowSettings] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(() => {
    const fromQuery = new URLSearchParams(location.search).get('projectId');
    return fromQuery || null;
  });
  const [projectDialogData, setProjectDialogData] = useState<Partial<TaskingBotProject> | null>(
    null,
  );
  const [chatSide, setChatSide] = useState<'left' | 'right'>(() => {
    try {
      return (localStorage.getItem('taskingbot-chat-side') as 'left' | 'right') || 'right';
    } catch {
      return 'right';
    }
  });
  const [mobilePanel, _setMobilePanel] = useState<MobilePanel>('tasks');
  const queryClient = useQueryClient();
  // Wrap setMobilePanel to refetch tasks when switching to tasks tab
  const setMobilePanel = useCallback((panel: MobilePanel) => {
    _setMobilePanel(panel);
    if (panel === 'tasks') {
      queryClient.invalidateQueries({ queryKey: ['taskingbot', 'tasks'] });
      queryClient.invalidateQueries({ queryKey: ['taskingbot', 'projects'] });
    }
  }, [queryClient]);
  const [focusedTask, setFocusedTask] = useState<FocusTaskContext | null>(null);
  const [detailTaskId, setDetailTaskId] = useState<string | null>(null);
  const [workspacePanelTab, setWorkspacePanelTab] = useState<MainPanelTab>('chat');
  const [taskHeaderInfo, setTaskHeaderInfo] = useState<TaskHeaderInfo | null>(null);
  const [manageTaskOpen, setManageTaskOpen] = useState(false);

  // ── Task search ──
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<TaskingBotTask[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchDropdownRef = useRef<HTMLDivElement>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const runSearch = useCallback(async (q: string) => {
    if (!q.trim()) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    try {
      const params = new URLSearchParams({ q: q.trim(), limit: '20' });
      if (selectedProjectId) params.set('projectId', selectedProjectId);
      const res = await fetch(`/_api/taskingbot/tasks?${params}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Search failed');
      const data = superjson.parse<{ tasks: TaskingBotTask[] }>(await res.text());
      setSearchResults(data.tasks);
    } catch {
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  }, [selectedProjectId]);

  const handleSearchInput = useCallback((value: string) => {
    setSearchQuery(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => runSearch(value), 250);
  }, [runSearch]);

  const openSearch = useCallback(() => {
    setSearchOpen(true);
    setTimeout(() => searchInputRef.current?.focus(), 50);
  }, []);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
  }, []);

  // Close search dropdown on outside click
  useEffect(() => {
    if (!searchOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        searchDropdownRef.current &&
        !searchDropdownRef.current.contains(e.target as Node)
      ) {
        closeSearch();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [searchOpen, closeSearch]);

  // Ctrl+K / Cmd+K to open search
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        if (searchOpen) closeSearch();
        else openSearch();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [searchOpen, openSearch, closeSearch]);

  const handleSearchResultClick = (task: TaskingBotTask) => {
    closeSearch();
    const meta = task.metadata ? JSON.parse(task.metadata) : {};
    setFocusedTask({
      id: task.id,
      title: task.title,
      parentTaskId: task.parentTaskId ?? meta.parentTaskId ?? null,
      nonce: Date.now(),
    });
    const params = new URLSearchParams(location.search);
    if (selectedProjectId) params.set('projectId', selectedProjectId);
    const query = params.toString();
    navigate(`/taskingbot/task/${encodeURIComponent(task.id)}${query ? `?${query}` : ''}`);
  };

  const activeView: TaskingBotView = location.pathname.startsWith('/taskingbot/board')
    ? 'board'
    : location.pathname.startsWith('/taskingbot/roadmap')
      ? 'roadmap'
      : location.pathname.startsWith('/taskingbot/tasksuite')
        ? 'tasksuite'
        : location.pathname.startsWith('/taskingbot/agents')
          ? 'agents'
          : 'workspace';

  // Fetch tasks for roadmap view
  const { data: allTasks } = useTaskingBotTasks();

  useEffect(() => {
    const fromQuery = new URLSearchParams(location.search).get('projectId');
    setSelectedProjectId(fromQuery || null);
  }, [location.search]);

  useEffect(() => {
    if (!routeTaskId) {
      setFocusedTask(null);
      return;
    }

    setFocusedTask((prev) => ({
      id: routeTaskId,
      title: prev?.id === routeTaskId ? prev.title : 'Task',
      parentTaskId: prev?.id === routeTaskId ? prev.parentTaskId ?? null : null,
      nonce: Date.now(),
    }));

    if (isMobile) {
      setMobilePanel('chat');
    }
  }, [routeTaskId, isMobile]);

  const handleOpenTaskInChat = (task: OpenInChatTask) => {
    const params = new URLSearchParams(location.search);
    if (selectedProjectId) params.set('projectId', selectedProjectId);
    else params.delete('projectId');

    setFocusedTask({
      id: task.id,
      title: task.title,
      parentTaskId: task.parentTaskId ?? null,
      nonce: Date.now(),
    });

    const query = params.toString();
    navigate(`/taskingbot/task/${encodeURIComponent(task.id)}${query ? `?${query}` : ''}`);
    if (isMobile) setMobilePanel('chat');
  };

  const chatPanel =
    activeView === 'workspace' && routeTaskId ? (
      <TaskingBotFocusedChat
        key={`task-${routeTaskId}`}
        projectId={selectedProjectId}
        focusTaskContext={focusedTask}
        onClearFocus={() => {
          setFocusedTask(null);
          setTaskHeaderInfo(null);
          const params = new URLSearchParams(location.search);
          if (selectedProjectId) params.set('projectId', selectedProjectId);
          else params.delete('projectId');
          const query = params.toString();
          navigate(`/taskingbot${query ? `?${query}` : ''}`);
        }}
        onTaskHeaderInfo={setTaskHeaderInfo}
        externalManageOpen={manageTaskOpen}
        onExternalManageClose={() => setManageTaskOpen(false)}
      />
    ) : (
      <TaskingBotChat
        key={`main-${selectedProjectId || 'global'}`}
        projectId={selectedProjectId}
        panelTab={workspacePanelTab}
        onPanelTabChange={setWorkspacePanelTab}
      />
    );

  useEffect(() => {
    const resetScroll = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      const main = document.querySelector('main');
      if (main instanceof HTMLElement) {
        main.scrollTop = 0;
        main.scrollLeft = 0;
      }
    };
    resetScroll();
    const raf = requestAnimationFrame(resetScroll);
    return () => cancelAnimationFrame(raf);
  }, [location.pathname]);

  const handleViewChange = (view: TaskingBotView) => {
    const paths: Record<TaskingBotView, string> = {
      workspace: '/taskingbot',
      board: '/taskingbot/board',
      tasksuite: '/taskingbot/tasksuite',
      roadmap: '/taskingbot/roadmap',
      agents: '/taskingbot/agents',
    };
    const targetPath = paths[view];
    if (location.pathname !== targetPath) {
      navigate(targetPath);
    }
  };

  return (
    <div className={styles.pageWrapper}>
      {showTutorial && !tutorialDismissed && (
        <OnboardingTutorial
          type="taskingbot"
          userId={currentUserId}
          onComplete={() => setTutorialDismissed(true)}
        />
      )}
      <div className={styles.container}>
        <title>TaskingBot | Tasking.tech</title>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            {routeTaskId && taskHeaderInfo ? (
              <>
                <button
                  onClick={() => {
                    setFocusedTask(null);
                    setTaskHeaderInfo(null);
                    const params = new URLSearchParams(location.search);
                    if (selectedProjectId) params.set('projectId', selectedProjectId);
                    else params.delete('projectId');
                    const query = params.toString();
                    navigate(`/taskingbot${query ? `?${query}` : ''}`);
                  }}
                  className={styles.taskBackBtn}
                  title="Back to tasks"
                >
                  <ArrowLeft size={16} />
                </button>
                <div className={styles.taskStatusIcon}>{getTaskStatusIcon(taskHeaderInfo.status)}</div>
                <h1 className={styles.taskTitleInline}>{taskHeaderInfo.title}</h1>
                <span className={`${styles.taskBadge} ${styles[`taskBadge_${taskHeaderInfo.status}`] || ''}`}>
                  {taskHeaderInfo.status === 'waiting_for_input' ? 'Needs Info' : taskHeaderInfo.status === 'in_progress' ? 'In Progress' : taskHeaderInfo.status}
                </span>
                {taskHeaderInfo.priority && (
                  <span className={`${styles.taskBadge} ${styles[`taskBadgePri_${taskHeaderInfo.priority}`] || ''}`}>
                    {taskHeaderInfo.priority}
                  </span>
                )}
                {taskHeaderInfo.progress !== undefined && taskHeaderInfo.progress > 0 && (
                  <span className={styles.taskProgress}>{taskHeaderInfo.progress}%</span>
                )}
                <button
                  onClick={() => setManageTaskOpen(true)}
                  className={styles.taskManageBtn}
                  title="Manage task"
                >
                  <Hammer size={15} />
                </button>
              </>
            ) : (
              <>
                <div className={styles.brandMark}>
                  <TaskingBotAvatarPortal
                    size="md"
                    alt="Tasking.tech"
                    className={styles.headerAvatarPortal}
                  />
                </div>
                <div className={styles.titleGroup}>
                  <p className={styles.kicker}>Autonomous execution layer</p>
                  <h1 className={styles.title}>TaskingBot</h1>
                  <p className={styles.subtitle}>
                    Plans tasks, installs skills, and coordinates agents in one command flow.
                  </p>
                  <div className={styles.capabilityRow}>
                    <span className={styles.capabilityPill}>
                      <span className={styles.pillDot} />
                      Multi-agent
                    </span>
                    <span className={styles.capabilityPill}>
                      <span className={styles.pillDot} />
                      Dynamic skill install
                    </span>
                    <span className={styles.capabilityPill}>
                      <span className={styles.pillDot} />
                      Production task queue
                    </span>
                  </div>
                </div>
              </>
            )}

            {/* Search bar — inside headerLeft so the icon sits next to the title */}
            <div className={styles.headerSearch} ref={searchDropdownRef}>
              {searchOpen ? (
              <div className={styles.searchInputWrap}>
                <Search size={14} className={styles.searchIcon} />
                <input
                  ref={searchInputRef}
                  type="text"
                  className={styles.searchInput}
                  placeholder={t.taskingbot.searchTasks}
                  value={searchQuery}
                  onChange={(e) => handleSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') closeSearch();
                  }}
                />
                <button className={styles.searchCloseBtn} onClick={closeSearch} title="Close search">
                  <X size={14} />
                </button>
                {(searchResults.length > 0 || searchLoading || searchQuery.trim()) && (
                  <div className={styles.searchDropdown}>
                    {searchLoading && (
                      <div className={styles.searchStatus}>Searching…</div>
                    )}
                    {!searchLoading && searchQuery.trim() && searchResults.length === 0 && (
                      <div className={styles.searchStatus}>No tasks found</div>
                    )}
                    {searchResults.map((task) => {
                      const meta = task.metadata ? (() => { try { return JSON.parse(task.metadata); } catch { return {}; } })() : {};
                      const subtasks: Array<{ id: string; title: string; completed: boolean }> = meta.subtasks || [];
                      return (
                        <button
                          key={task.id}
                          className={styles.searchResult}
                          onClick={() => handleSearchResultClick(task)}
                        >
                          <div className={styles.searchResultMain}>
                            <span className={`${styles.searchResultStatus} ${styles[`status_${task.status}`] || ''}`} />
                            <span className={styles.searchResultTitle}>{task.title}</span>
                            <span className={styles.searchResultPriority}>{task.priority}</span>
                          </div>
                          {task.description && (
                            <div className={styles.searchResultDesc}>
                              {task.description}
                            </div>
                          )}
                          {subtasks.length > 0 && (
                            <div className={styles.searchResultSubtasks}>
                              {subtasks.map((st) => (
                                <span key={st.id} className={`${styles.searchSubtask} ${st.completed ? styles.searchSubtaskDone : ''}`}>
                                  {st.completed ? '✓' : '○'} {st.title}
                                </span>
                              ))}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <button className={styles.searchToggle} onClick={openSearch} title="Search tasks (Ctrl+K)">
                <Search size={14} />
              </button>
            )}
          </div>
          </div>

          <div className={styles.headerActions}>
            <div className={styles.inlineTabs} role="tablist" aria-label="TaskingBot views">
              <button
                role="tab"
                aria-selected={activeView === 'workspace'}
                className={`${styles.viewTab} ${activeView === 'workspace' ? styles.viewTabActive : ''}`}
                onClick={() => {
                  setWorkspacePanelTab('chat');
                  handleViewChange('workspace');
                }}
              >
                <Columns3 size={14} />
                {t.taskingbot.tasks}
              </button>
              <button
                role="tab"
                aria-selected={activeView === 'board'}
                className={`${styles.viewTab} ${activeView === 'board' ? styles.viewTabActive : ''}`}
                onClick={() => handleViewChange('board')}
              >
                <KanbanSquare size={14} />
                TaskFlow
              </button>
              <button
                role="tab"
                aria-selected={activeView === 'roadmap'}
                className={`${styles.viewTab} ${activeView === 'roadmap' ? styles.viewTabActive : ''}`}
                onClick={() => handleViewChange('roadmap')}
              >
                <BarChart3 size={14} />
                Roadmap
              </button>
              <button
                role="tab"
                aria-selected={activeView === 'tasksuite'}
                className={`${styles.viewTab} ${activeView === 'tasksuite' ? styles.viewTabActive : ''}`}
                onClick={() => handleViewChange('tasksuite')}
              >
                <CalendarDays size={14} />
                {t.taskingbot.schedule}
              </button>
              <button
                role="tab"
                aria-selected={activeView === 'agents'}
                className={`${styles.viewTab} ${activeView === 'agents' ? styles.viewTabActive : ''}`}
                onClick={() => handleViewChange('agents')}
              >
                <Radar size={14} />
                Agents
              </button>
              <button
                role="tab"
                aria-selected={workspacePanelTab === 'assets'}
                className={`${styles.viewTab} ${workspacePanelTab === 'assets' ? styles.viewTabActive : ''}`}
                onClick={() => {
                  setWorkspacePanelTab('assets');
                }}
                title={t.taskingbot.assets}
              >
                <FolderKanban size={14} />
                {t.taskingbot.assets}
              </button>
            </div>
            {!isMobile && (activeView === 'workspace' || activeView === 'tasksuite' || activeView === 'board' || activeView === 'roadmap' || activeView === 'agents') && (
              <button
                className={styles.settingsBtn}
                onClick={() => {
                  const next = chatSide === 'right' ? 'left' : 'right';
                  setChatSide(next);
                  try {
                    localStorage.setItem('taskingbot-chat-side', next);
                  } catch {}
                }}
                title={`Move chat to ${chatSide === 'right' ? 'left' : 'right'}`}
              >
                {chatSide === 'right' ? (
                  <PanelLeftClose size={16} />
                ) : (
                  <PanelRightClose size={16} />
                )}
              </button>
            )}
            <button
              className={styles.settingsBtn}
              onClick={() => setShowShareDialog(true)}
              title="Share"
            >
              <Share2 size={16} />
            </button>
            <button
              className={styles.settingsBtn}
              onClick={() => setShowSettings(true)}
              title="Settings - Channels, Integrations, Skills"
            >
              <Settings size={16} />
            </button>
          </div>
        </div>

        {showSettings && <TaskingBotSettingsModal onClose={() => setShowSettings(false)} />}
        <ShareDialog
          open={showShareDialog}
          onOpenChange={setShowShareDialog}
          resourceType="conversation"
          resourceId={`taskingbot:${currentUserId || 'default'}`}
          resourceName="TaskingBot Workspace"
        />

        <div className={styles.contentArea}>
          {!isMobile && (
            <div className={`${styles.desktopWorkspace} ${chatSide === 'left' ? styles.chatOnLeft : ''} ${activeView === 'board' ? styles.boardLayout : ''} ${activeView === 'roadmap' ? styles.scheduleLayout : ''} ${activeView === 'tasksuite' ? styles.scheduleLayout : ''} ${activeView === 'agents' ? styles.scheduleLayout : ''}`}
              >
                {chatSide === 'left' && activeView === 'workspace' ? (
                  <>
                    <section className={styles.chatPanel}>
                      {chatPanel}
                    </section>
                    <section className={`${styles.tasksPanel} ${styles.tasksPanelRight}`}>
                      <TaskingBotTasksStatus
                        projectId={selectedProjectId}
                        onOpenInChat={handleOpenTaskInChat}
                      />
                    </section>
                  </>
                ) : activeView === 'board' ? (
                  chatSide === 'left' ? (
                    <>
                      <section className={styles.chatPanel}>
                        {chatPanel}
                      </section>
                      <section className={styles.tasksPanel}>
                        <TaskingBotTasksStatus
                          projectId={selectedProjectId}
                          onOpenInChat={handleOpenTaskInChat}
                        />
                      </section>
                      <section className={styles.taskBoardPage}>
                        <TaskFlow projectId={selectedProjectId} />
                      </section>
                    </>
                  ) : (
                    <>
                      <section className={styles.tasksPanel}>
                        <TaskingBotTasksStatus
                          projectId={selectedProjectId}
                          onOpenInChat={handleOpenTaskInChat}
                        />
                      </section>
                      <section className={styles.taskBoardPage}>
                        <TaskFlow projectId={selectedProjectId} />
                      </section>
                      <section className={styles.chatPanel}>
                        {chatPanel}
                      </section>
                    </>
                  )
                ) : activeView === 'roadmap' ? (
                  chatSide === 'left' ? (
                    <>
                      <section className={styles.chatPanel}>
                        {chatPanel}
                      </section>
                      <section className={styles.taskBoardPage}>
                        <TaskflowView tasks={allTasks || []} onTaskClick={(id) => handleOpenTaskInChat({ id, title: '', parentTaskId: null })} />
                      </section>
                    </>
                  ) : (
                    <>
                      <section className={styles.taskBoardPage}>
                        <TaskflowView tasks={allTasks || []} onTaskClick={(id) => handleOpenTaskInChat({ id, title: '', parentTaskId: null })} />
                      </section>
                      <section className={styles.chatPanel}>
                        {chatPanel}
                      </section>
                    </>
                  )
                ) : activeView === 'tasksuite' ? (
                  chatSide === 'left' ? (
                    <>
                      <section className={styles.chatPanel}>
                        {chatPanel}
                      </section>
                      <section className={styles.taskBoardPage}>
                        <TaskSchedule projectId={selectedProjectId} />
                      </section>
                    </>
                  ) : (
                    <>
                      <section className={styles.taskBoardPage}>
                        <TaskSchedule projectId={selectedProjectId} />
                      </section>
                      <section className={styles.chatPanel}>
                        {chatPanel}
                      </section>
                    </>
                  )
                ) : activeView === 'agents' ? (
                  chatSide === 'left' ? (
                    <>
                      <section className={styles.chatPanel}>
                        {chatPanel}
                      </section>
                      <section className={styles.taskBoardPage}>
                        <TaskingBotAgentsPanel />
                      </section>
                    </>
                  ) : (
                    <>
                      <section className={styles.taskBoardPage}>
                        <TaskingBotAgentsPanel />
                      </section>
                      <section className={styles.chatPanel}>
                        {chatPanel}
                      </section>
                    </>
                  )
                ) : (
                  <>
                    <section className={styles.tasksPanel}>
                      <TaskingBotTasksStatus
                        projectId={selectedProjectId}
                        onOpenInChat={handleOpenTaskInChat}
                      />
                    </section>
                    <section className={styles.chatPanel}>
                      {chatPanel}
                    </section>
                  </>
                )}
            </div>
          )}

          {isMobile && (
            <>
              {/* Panel content — takes all available space */}
              <div className={styles.mobilePanelContent}>
                {/* Tasks panel — hidden via CSS when chat is active so it doesn't unmount */}
                <div style={{ display: mobilePanel === 'tasks' ? 'flex' : 'none', flex: '1 1 0%', minHeight: 0, flexDirection: 'column', overflow: 'hidden' }}>
                  <>
                    <div className={styles.mobileViewTabs}>
                      {([
                        { view: 'workspace' as TaskingBotView, icon: <ListChecks size={13} />, label: 'Tasks' },
                        { view: 'board' as TaskingBotView, icon: <KanbanSquare size={13} />, label: 'TaskFlow' },
                        { view: 'roadmap' as TaskingBotView, icon: <BarChart3 size={13} />, label: 'Roadmap' },
                        { view: 'tasksuite' as TaskingBotView, icon: <CalendarDays size={13} />, label: 'Schedule' },
                      ] as const).map(({ view, icon, label }) => (
                        <button
                          key={view}
                          className={`${styles.mobileViewTab} ${activeView === view && workspacePanelTab !== 'assets' ? styles.mobileViewTabActive : ''}`}
                          onClick={() => {
                            setWorkspacePanelTab('chat');
                            handleViewChange(view);
                          }}
                        >
                          {icon}
                          <span>{label}</span>
                        </button>
                      ))}
                      <button
                        className={`${styles.mobileViewTab} ${workspacePanelTab === 'assets' ? styles.mobileViewTabActive : ''}`}
                        onClick={() => {
                          setWorkspacePanelTab('assets');
                          handleViewChange('workspace');
                        }}
                      >
                        <FolderKanban size={13} />
                        <span>Assets</span>
                      </button>
                    </div>
                    {workspacePanelTab === 'assets' ? (
                      <ChatAttachmentsLibrary
                        sessionId="__all__"
                        isOpen={true}
                        onClose={() => setWorkspacePanelTab('chat')}
                        variant="inline"
                      />
                    ) : activeView === 'workspace' ? (
                      <TaskingBotTasksStatus
                        projectId={selectedProjectId}
                        onOpenInChat={handleOpenTaskInChat}
                      />
                    ) : activeView === 'board' ? (
                      <TaskFlow projectId={selectedProjectId} />
                    ) : activeView === 'roadmap' ? (
                      <TaskflowView tasks={allTasks || []} onTaskClick={(id) => handleOpenTaskInChat({ id, title: '', parentTaskId: null })} />
                    ) : (
                      <TaskSchedule projectId={selectedProjectId} />
                    )}
                  </>
                </div>
                {/* Chat panel — ALWAYS rendered, hidden via CSS when tasks panel is active.
                    This prevents unmounting the chat component which would kill active streams. */}
                {/* Assets panel */}
                <div style={{ display: mobilePanel === 'assets' ? 'flex' : 'none', flex: '1 1 0%', minHeight: 0, flexDirection: 'column', overflow: 'hidden' }}>
                  <ChatAttachmentsLibrary
                    sessionId="__all__"
                    isOpen={mobilePanel === 'assets'}
                    onClose={() => setMobilePanel('tasks')}
                    variant="inline"
                  />
                </div>
                <div style={{ display: mobilePanel === 'chat' ? 'flex' : 'none', flex: '1 1 0%', minHeight: 0, flexDirection: 'column' }}>
                  {chatPanel}
                </div>
              </div>

              {/* ── Bottom navigation bar ── */}
              <nav className={styles.mobilePanelNav}>
                {([
                  { key: 'tasks' as MobilePanel, icon: <ListChecks size={18} />, label: 'Tasks' },
                  { key: 'chat' as MobilePanel, icon: <MessageSquareText size={18} />, label: 'Chat' },
                  { key: 'assets' as MobilePanel, icon: <FolderKanban size={18} />, label: 'Assets' },
                ] as const).map(({ key, icon, label }) => (
                  <button
                    key={key}
                    className={`${styles.mobilePanelTab} ${mobilePanel === key ? styles.mobilePanelTabActive : ''}`}
                    onClick={() => setMobilePanel(key)}
                  >
                    {icon}
                    <span>{label}</span>
                  </button>
                ))}
              </nav>
            </>
          )}
        </div>
      </div>

      {isMobile ? <SessionNavToolbar side="left" /> : <SessionNavToolbar side="right" />}

      {/* Project create/edit dialog */}
      {projectDialogData && (
        <TaskingBotProjectDialog
          project={projectDialogData}
          onClose={() => setProjectDialogData(null)}
        />
      )}

      {/* Task detail modal — opened from sidebar task click */}
      {detailTaskId && (
        <TaskDetailModal
          taskId={detailTaskId}
          open={!!detailTaskId}
          onOpenChange={(open) => { if (!open) setDetailTaskId(null); }}
        />
      )}
    </div>
  );
}
